import { css } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { MouseEventHandler } from "react";
import React from "react";
import type { DesignSystemLinkHref, ShowLinkAsActive } from "../../routing/index";
import { useIsUrlActive, useOctopusLinkComponent } from "../../routing/index";
interface AreaNavListLinkProps {
    href: DesignSystemLinkHref;
    showLinkAsActive?: ShowLinkAsActive;
    label: string;
    accessibleName?: string;
    onClick?: MouseEventHandler<Element>;
    icon?: React.ReactNode;
}
export function AreaNavListLink({ href, showLinkAsActive, label, accessibleName, onClick, icon }: AreaNavListLinkProps) {
    const Link = useOctopusLinkComponent();
    const isUrlActive = useIsUrlActive();
    const isActive = isUrlActive(href, showLinkAsActive ?? "if path partially matches");
    return (<Link className={areaNavListLinkStyles} aria-current={isActive ? "page" : undefined} href={href} aria-label={accessibleName} onClick={onClick} title={label}>
            {icon && <div>{icon}</div>}
            <span className={areaNavListLinkTextStyles}>{label}</span>
        </Link>);
}
const areaNavListLinkStyles = css({
    display: "flex",
    width: "100%",
    height: "2rem",
    alignItems: "center",
    gap: space["8"],
    textDecoration: "none",
    font: text.regular.default.medium,
    color: themeTokens.color.navList.text.default,
    backgroundColor: themeTokens.color.navList.background.default,
    padding: `${space["4"]} ${space["8"]}`,
    borderRadius: space["4"],
    ":hover": {
        color: themeTokens.color.navList.text.hover,
        backgroundColor: themeTokens.color.navList.background.hover,
    },
    ":active": {
        color: themeTokens.color.navList.text.active,
        background: themeTokens.color.navList.background.active,
    },
    ":focus-visible": {
        boxShadow: themeTokens.shadow.focused,
        outline: "none",
    },
    "&[aria-current=page]": {
        backgroundColor: themeTokens.color.navList.background.active,
        color: themeTokens.color.navList.text.active,
    },
});
const areaNavListLinkTextStyles = css({
    // Needs a minWidth set to allow for the ellipsis text truncation within a flexbox
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "clip",
    textOverflow: "ellipsis",
});
